// MUI-free copy of Ui-components/mvp-development/MvpEndToEndServices.
import {memo} from 'react';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import ArrowButton from '@/components/atoms/ArrowButton';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './MvpEndToEndServices.module.scss';

interface MvpService {
    title: string;
    description: string;
    deliverables?: string[];
}

type ServicesVariant = 'blue' | 'white';

interface MvpEndToEndServicesProps {
    heading: string;
    subheading: string;
    imageSrc: string | StaticImageData;
    imageCaptionTitle: string;
    services: MvpService[];
    /** Optional button under the list. Renders only when both are supplied. */
    ctaText?: string;
    onCtaClick?: () => void;
    /** `blue` (default) is the navy section; `white` is the same layout with dark text on a
     *  light ground — the wrapper's background class shows through, white if it has none. */
    variant?: ServicesVariant;
}

// Literal names so PurgeCSS keeps each class. Only colours differ between the variants.
const VARIANT_CLASS = {blue: styles.blue, white: styles.white} as const;

const IMAGE_STYLE = {objectFit: 'cover'} as const;

const MvpEndToEndServices = ({
    heading,
    subheading,
    imageSrc,
    imageCaptionTitle,
    services,
    ctaText,
    onCtaClick,
    variant = 'blue'
}: MvpEndToEndServicesProps) => (
    <div className={`${styles.section} ${VARIANT_CLASS[variant]}`}>
        <Container>
            <div className={styles.layout}>
                <div>
                    <div className={styles.imageWrap}>
                        <OptimizedImage
                            src={imageSrc}
                            alt={imageCaptionTitle}
                            fill
                            sizes="(max-width: 900px) 100vw, 30vw"
                            style={IMAGE_STYLE}
                        />
                        <div className={styles.captionBox}>
                            <p className={`font-headers ${styles.captionTitle}`}>{imageCaptionTitle}</p>
                        </div>
                    </div>
                </div>

                <div>
                    <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                    <p className={`font-regular-text ${styles.subheading}`}>{parse(subheading)}</p>

                    <div>
                        {services?.map((service, idx) => (
                            <div key={service.title} className={styles.row}>
                                <div className={styles.item}>
                                    <span className={`font-headers svc-number ${styles.number}`}>{String(idx + 1).padStart(2, '0')}</span>
                                    <h3 className={`font-headers svc-text ${styles.title}`}>{service.title}</h3>
                                    <div>
                                        <p className={`font-regular-text svc-text ${styles.desc}`}>{service.description}</p>
                                        {service.deliverables && service.deliverables.length > 0 && (
                                            <div className={styles.tagRow}>
                                                {service.deliverables.map((item) => (
                                                    <span key={item} className={`font-headers ${styles.tag}`}>
                                                        {parse(item)}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {ctaText && onCtaClick && (
                        <div className={styles.ctaWrap}>
                            <ArrowButton variant="primary" icon="ArrowOutward" bounce={false} onClick={onCtaClick}>
                                {ctaText}
                            </ArrowButton>
                        </div>
                    )}
                </div>
            </div>
        </Container>
    </div>
);

export default memo(MvpEndToEndServices);
